import React from 'react';
import { split } from '@apollo/client/link/core';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { HttpLink } from '@apollo/client/link/http';
import { InMemoryCache } from '@apollo/client/cache';
import { ApolloClient } from '@apollo/client';
import { ApolloProvider } from '@apollo/client/react';
import { FederatedMultiLink } from './federated-multi-link.js';
const windowObject = () => (typeof window !== 'undefined' ? window : {});
const appNameHeader = 'x-sauron-app-name';
const appVersionHeader = 'x-sauron-app-version';
const getClientConfig = (field) => { var _a, _b; return (_b = (_a = windowObject()) === null || _a === void 0 ? void 0 : _a.AT_SPA_JS_CONFIG) === null || _b === void 0 ? void 0 : _b[field]; };
const fetchFactory = (configuration) => {
    return (uri, options) => {
        if ((configuration === null || configuration === void 0 ? void 0 : configuration.ssrMode) && (configuration === null || configuration === void 0 ? void 0 : configuration.debugRequests)) {
            console.warn('GraphQL fetch called', uri, options);
        }
        let queryParams = '';
        try {
            const operation = options !== undefined && JSON.parse(options.body);
            if (operation) {
                if (operation instanceof Array) {
                    const operationName = operation.map((operation) => operation.operationName).join('&opname=');
                    queryParams = operationName ? `?opname=${operationName}` : '';
                }
                else {
                    queryParams = operation.operationName ? `?opname=${operation.operationName}` : '';
                }
            }
            if ((configuration === null || configuration === void 0 ? void 0 : configuration.appendOriginator) === true && windowObject().AT_REQUEST_ID) {
                const originatorParam = `originator=${windowObject().AT_REQUEST_ID}`;
                if (queryParams.length)
                    queryParams += `&${originatorParam}`;
                else
                    queryParams = `?${originatorParam}`;
            }
        }
        catch (err) { }
        const fetchFn = configuration && configuration.fetch ? configuration.fetch : fetch;
        return fetchFn(`${uri}${queryParams}`, options);
    };
};
const createBatchableLink = (config) => {
    const { ssrMode = false } = config;
    const { batchingEnabled = !ssrMode } = config;
    const httpLink = new HttpLink(config);
    let link = httpLink;
    if (batchingEnabled) {
        const batchHttpLink = new BatchHttpLink(config);
        link = split((operation) => operation.getContext().important === true, httpLink, batchHttpLink);
    }
    return link;
};
export const createApolloClient = (configuration) => {
    const config = Object.assign(Object.assign({ uri: '/at-graphql', gatewayUri: '/at-gateway', secureGatewayUri: '/at-secure-gateway' }, (configuration || {})), { fetch: fetchFactory(configuration) });
    if (config.headers === undefined) {
        config.headers = {};
    }
    const { cache: customCache, appName, appVersion, ssrMode } = config;
    delete config['cache'];
    delete config['appName'];
    delete config['appVersion'];
    if (!config.headers[appNameHeader]) {
        config.headers[appNameHeader] = appName || getClientConfig('appName') || 'unknown';
    }
    if (!config.headers[appVersionHeader]) {
        config.headers[appVersionHeader] = appVersion || getClientConfig('appVersion') || 'unknown';
    }
    const links = new Map();
    links.set('external', createBatchableLink(Object.assign(Object.assign({}, config), { uri: config.gatewayUri })));
    links.set('secure', createBatchableLink(Object.assign(Object.assign({}, config), { uri: config.secureGatewayUri })));
    links.set('default', createBatchableLink(config));
    const link = new FederatedMultiLink(links);
    const cache = customCache !== null && customCache !== void 0 ? customCache : new InMemoryCache();
    if (!ssrMode && windowObject().AT_APOLLO_STATE) {
        cache.restore(windowObject().AT_APOLLO_STATE);
    }
    const connectToDevTools = process.env.NODE_ENV === 'development' && !ssrMode;
    const client = new ApolloClient(Object.assign({ link,
        cache,
        connectToDevTools }, config));
    return client;
};
export const withGraphQL = (WrappedComponent, configuration) => {
    var _a;
    const client = (_a = configuration === null || configuration === void 0 ? void 0 : configuration.client) !== null && _a !== void 0 ? _a : createApolloClient(configuration);
    const WithGraphQL = (props) => (React.createElement(ApolloProvider, { client: client },
        React.createElement(WrappedComponent, Object.assign({}, props))));
    return WithGraphQL;
};
